import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Scale, Shield, CreditCard, AlertTriangle, Clock } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import { BRAND_OPERATOR_LINE, COMPANY, formatCompanyMailingAddress } from '@/lib/companyInfo';


const Terms = () => {
  return (
    <>
      <SeoHead
        title="Terms of Service - Wage Atlas | User Agreement & Terms"
        description="Wage Atlas terms of service and user agreement. Learn about acceptable use, our free products, and legal obligations when using our salary research platform."
        canonical="https://wageatlas.com/terms/"
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Scale className="h-8 w-8 text-primary" />
                <h1 className="text-3xl font-bold">Terms of Service</h1>
              </div>
              <p className="text-lg text-muted-foreground">
                Please read these terms carefully before using Wage Atlas services.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Last updated: June 2026</span>
              </div>
            </div>

            {/* Operator */}
            <Card>
              <CardHeader>
                <CardTitle>Operator</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-muted-foreground">
                <p>{BRAND_OPERATOR_LINE}</p>
                <p>
                  <strong>Legal entity:</strong> {COMPANY.legalName}
                </p>
                <p>
                  <strong>Mailing address:</strong> {formatCompanyMailingAddress()}
                </p>
              </CardContent>
            </Card>

            {/* Acceptance of Terms */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Acceptance of Terms
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  By accessing or using {COMPANY.brandName} (&quot;we,&quot; &quot;our,&quot; or &quot;the Service&quot;), you agree to be bound by these Terms of Service. 
                  If you do not agree to these terms, please do not use our services.
                </p>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>Important:</strong> These terms constitute a legally binding agreement between you and {COMPANY.legalName}
                    {' '}(operator of the {COMPANY.brandName} brand). Your use of our services indicates your acceptance of all terms and conditions.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Service Description */}
            <Card>
              <CardHeader>
                <CardTitle>Service Description</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Wage Atlas provides salary research tools, career insights, and wage analysis services including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Salary data and wage comparisons across occupations and locations</li>
                  <li>Career trend analysis and labor market insights</li>
                  <li>Salary calculators and career planning tools</li>
                  <li>Free PDF reports for a single occupation and location, delivered by email</li>
                </ul>
                <p className="text-muted-foreground">
                  All salary data, percentiles, cost-of-living adjustments, historical trends, maps and career
                  tools are free to use. Wage Atlas is supported by advertising and partner referrals.
                </p>
              </CardContent>
            </Card>

            {/* Non-Affiliation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Non-Affiliation with Government Agencies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-200 font-semibold mb-2">
                    Important Notice
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Wage Atlas is an independent service provider and is not affiliated with, endorsed by, 
                    sponsored by, or in any way officially connected with the United States Government, 
                    the U.S. Bureau of Labor Statistics, the Department of Labor, or any other government 
                    agency.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Data Attribution</h3>
                  <p className="text-muted-foreground">
                    While our salary data is derived from publicly available sources including the U.S. Bureau 
                    of Labor Statistics, any reference to government data sources is for attribution purposes 
                    only and does not imply any relationship, endorsement, or approval. Wage Atlas independently 
                    compiles, analyzes, and presents this data through our proprietary platform.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">No Government Endorsement</h3>
                  <p className="text-muted-foreground">
                    The use of government data does not constitute an endorsement by any government agency 
                    of Wage Atlas, our services, or our analysis. We are a private, independent company 
                    providing salary research and career insights based on publicly available information.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Paid Partnerships */}
            <Card>
              <CardHeader>
                <CardTitle>Paid Partnerships &amp; Sponsored Placements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Some modules on {COMPANY.brandName} — such as training program spotlights or partner career resources — are
                  paid placements from third-party organizations. Sponsored content is clearly labeled (e.g., &quot;Sponsored&quot;
                  or &quot;Paid partnership&quot;) and outbound links may use sponsored link attributes.
                </p>
                <p className="text-muted-foreground">
                  {COMPANY.brandName} does not employ, place, or guarantee jobs through sponsor programs. Third-party partners
                  are solely responsible for their program claims, applications, hiring, and training outcomes. Listing a partner
                  does not imply that Meta, Google, government agencies, or other third parties endorse {COMPANY.legalName}{' '}
                  or {COMPANY.brandName}.
                </p>
              </CardContent>
            </Card>

            {/* Affiliate Relationships */}
            <Card>
              <CardHeader>
                <CardTitle>Affiliate Relationships & Disclosure</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Wage Atlas participates in affiliate marketing programs with various third-party service providers, 
                  including job boards, education platforms, resume services, and career resources. When you click 
                  on affiliate links on our website and make a purchase or sign up for a service, we may receive a 
                  commission at no additional cost to you.
                </p>
                <div>
                  <h3 className="font-semibold mb-2">Affiliate Disclosure</h3>
                  <p className="text-muted-foreground">
                    We clearly disclose our affiliate relationships where applicable. Affiliate commissions help 
                    us maintain and improve Wage Atlas while keeping core features free for users. Our recommendations 
                    are based on our independent assessment of value to users and are not influenced solely by 
                    commission potential.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Third-Party Services</h3>
                  <p className="text-muted-foreground">
                    When you use affiliate links to access third-party services, you are subject to those services' 
                    terms of service and privacy policies. Wage Atlas is not responsible for the content, policies, 
                    or practices of third-party websites or services accessed through affiliate links.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* User Accounts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  User Accounts & Responsibilities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Account Creation</h3>
                  <p className="text-muted-foreground mb-2">
                    You may need to create an account to access certain features. You agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Notify us immediately of unauthorized access</li>
                    <li>Accept responsibility for all activities under your account</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Acceptable Use</h3>
                  <p className="text-muted-foreground mb-2">You agree not to:</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Use the service for illegal or unauthorized purposes</li>
                    <li>Violate any laws or regulations in your jurisdiction</li>
                    <li>Interfere with or disrupt the service or servers</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Use automated tools to scrape or harvest data without permission</li>
                    <li>Reproduce, distribute, or create derivative works without consent</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Payments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Payments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Free Service</h3>
                  <p className="text-muted-foreground">
                    Wage Atlas does not currently offer any paid products or subscriptions. All salary data,
                    tools, and PDF reports are free. Free PDF reports are delivered by email; by requesting one,
                    you also subscribe to our newsletter, which you can unsubscribe from at any time.
                  </p>
                </div>

                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">
                    For refund details on past purchases, please see our{' '}
                    <Link to="/refund-policy" className="text-primary hover:underline">
                      Refund Policy
                    </Link>
                    .
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Historical Purchases</h3>
                  <p className="text-muted-foreground">
                    Payments for previously offered paid products (such as one-time PDF reports) were handled
                    securely by a trusted third-party payment processor. Refunds for eligible historical
                    purchases are governed by our Refund Policy.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Intellectual Property */}
            <Card>
              <CardHeader>
                <CardTitle>Intellectual Property</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Our Content</h3>
                  <p className="text-muted-foreground">
                    All content, features, and functionality are owned by {COMPANY.legalName} and protected by copyright, 
                    trademark, and other intellectual property laws. This includes our analysis, visualizations, 
                    and proprietary algorithms.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Data Sources</h3>
                  <p className="text-muted-foreground">
                    Our salary data is derived from public sources including the U.S. Bureau of Labor Statistics. 
                    While the underlying data is public, our analysis, presentation, and insights are proprietary.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Limited License</h3>
                  <p className="text-muted-foreground">
                    We grant you a limited, non-exclusive, non-transferable license to use our services for personal 
                    or business purposes in accordance with these terms.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Disclaimers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Disclaimers & Limitations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                  <h3 className="font-semibold mb-2 text-amber-800 dark:text-amber-200">Warranty Disclaimer</h3>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                    <strong>THE SERVICE AND ALL DATA, INFORMATION, AND CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE" 
                    WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED 
                    WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ACCURACY.</strong>
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {COMPANY.legalName.toUpperCase()} DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT 
                    DEFECTS WILL BE CORRECTED.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Data Accuracy & Completeness</h3>
                  <p className="text-muted-foreground">
                    While we strive for accuracy, salary data is provided "as is" and may not reflect current market 
                    conditions. Data is compiled from various sources and may be outdated, incomplete, or contain errors. 
                    We make no guarantees regarding the accuracy, completeness, or timeliness of any data, information, or 
                    content provided through the Service. All data should be used for informational purposes only and should 
                    not be the sole basis for making financial, career, or business decisions.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Service Availability</h3>
                  <p className="text-muted-foreground">
                    We aim to provide uninterrupted service but cannot guarantee 100% uptime. We reserve the right to 
                    modify, suspend, or discontinue any part of our services with or without notice. We do not warrant 
                    that the Service will meet your requirements or that the operation of the Service will be uninterrupted 
                    or error-free.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">No Professional Advice</h3>
                  <p className="text-muted-foreground">
                    The information provided through Wage Atlas is for general informational purposes only and does not 
                    constitute professional, financial, legal, or career advice. You should consult with qualified 
                    professionals before making any decisions based on information obtained from our Service.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Limitation of Liability */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Limitation of Liability
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 dark:bg-red-950/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                  <h3 className="font-semibold mb-2 text-red-800 dark:text-red-200">Exclusion of Damages</h3>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY.legalName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY 
                    DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT 
                    NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING 
                    OUT OF OR RELATING TO YOUR USE OF OR INABILITY TO USE THE SERVICE.</strong>
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Maximum Liability Cap</h3>
                  <p className="text-muted-foreground">
                    To the maximum extent permitted by law, {COMPANY.legalName}&apos;s total liability to you for all claims arising 
                    from or related to the use of the Service shall not exceed the amount you paid to {COMPANY.legalName} in the 
                    twelve (12) months immediately preceding the event giving rise to such liability, or one hundred 
                    dollars ($100), whichever is greater.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Scope of Limitations</h3>
                  <p className="text-muted-foreground">
                    These limitations apply regardless of the legal theory on which the claim is based, whether in contract, 
                    tort (including negligence), strict liability, or otherwise, and even if {COMPANY.legalName} has been advised 
                    of the possibility of such damages. Some jurisdictions do not allow the exclusion or limitation of 
                    incidental or consequential damages, so the above limitations may not apply to you.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">No Liability for Third-Party Content</h3>
                  <p className="text-muted-foreground">
                    Wage Atlas shall not be liable for any errors, omissions, or inaccuracies in data obtained from 
                    third-party sources, including but not limited to government databases, or for any decisions made 
                    based on such data.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Indemnification */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Indemnification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <p className="text-muted-foreground mb-3">
                    You agree to indemnify, defend, and hold harmless {COMPANY.legalName}, its officers, directors, employees, 
                    agents, and affiliates from and against any and all claims, damages, losses, costs, expenses, 
                    liabilities, and fees (including reasonable attorneys' fees) arising from or relating to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Your use of the Service</li>
                    <li>Your violation of these Terms of Service</li>
                    <li>Your violation of any rights of another party, including intellectual property rights</li>
                    <li>Your violation of any applicable laws or regulations</li>
                    <li>Any decisions, actions, or consequences resulting from your use of data or information obtained through the Service</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Cooperation in Defense</h3>
                  <p className="text-muted-foreground">
                    You agree to cooperate fully in the defense of any claim for which indemnification is sought. Wage 
                    Atlas reserves the right, at its own expense, to assume the exclusive defense and control of any matter 
                    subject to indemnification by you.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Survival</h3>
                  <p className="text-muted-foreground">
                    Your indemnification obligations will survive the termination of these Terms and your use of the Service.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Termination */}
            <Card>
              <CardHeader>
                <CardTitle>Termination</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Either party may terminate this agreement at any time:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li><strong>By You:</strong> Stop using our services and delete your account</li>
                  <li><strong>By Us:</strong> For violation of terms, illegal activity, or business reasons</li>
                  <li><strong>Effect:</strong> Termination ends your right to access your account and any paid reports</li>
                  <li><strong>Data:</strong> We may delete your account data after termination</li>
                </ul>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card>
              <CardHeader>
                <CardTitle>Governing Law & Disputes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  These terms are governed by the laws of the State of {COMPANY.formationState}, United States, without regard to conflict of law principles. 
                  Any disputes will be resolved through binding arbitration or in a court of competent jurisdiction in the State of {COMPANY.formationState}.
                </p>
                
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Contact for Legal Matters</h3>
                  <p className="text-muted-foreground">
                    For legal questions or to report violations:{' '}
                    <a href={`mailto:${COMPANY.emails.legal}`} className="text-primary hover:underline">
                      {COMPANY.emails.legal}
                    </a>
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {COMPANY.legalName} — {formatCompanyMailingAddress()}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Changes to Terms */}
            <Card>
              <CardHeader>
                <CardTitle>Changes to Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We may update these terms periodically. Material changes will be communicated via email or 
                  prominent notice on our website. Continued use after changes constitutes acceptance of updated terms.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;